import React from 'react'
import Resource from './Resource/index.js'
import SlotLabel from './SlotLabel/index.js'
import Appointment from './Appointment/index.js'
import AppointmentCandidate from './AppointmentCandidate/index.js'
import AppointmentCandidateShadow from './AppointmentCandidateShadow/index.js'
import AppointmentCandidateUntemplated from './AppointmentCandidateUntemplated/index.js'
import AppointmentCandidateUntemplatedHover from './AppointmentCandidateUntemplatedHover/index.js'
import Note from './Note/index.js'
import BlockedTime from './BlockedTime/index.js'
import AppointmentType from './AppointmentType/index.js'
import AppointmentTypeCandidate from './AppointmentTypeCandidate/index.js'
import Closed from './Closed/index.js'
import { useDataValue } from 'Simple/Data.js'
import { currentTzDate, formatTzDate } from 'Data/format.js'

export default function useCustomComponentRender(props) {
  let timeZoneId = useDataValue({
    context: 'timegrid',
    path: 'location.time_zone_id',
    viewPath: props.viewPath,
  })

  /**
   * Render event custom components
   * @param {import('@fullcalendar/core').EventContentArg} value
   */
  function renderEventContent(value) {
    switch (value.event.extendedProps.type) {
      case 'appointment':
        return (
          <Appointment
            key={value.event.id}
            event={{
              id: value.event.id,
              title: value.event.title,
              start: value.event.startStr,
              end: value.event.endStr,
              ...value.event.extendedProps,
            }}
            viewPath={`${props.viewPath}/Appointment(${value.event.id})`}
          />
        )
      case 'note':
        return (
          <Note
            key={value.event.id}
            viewPath={`${props.viewPath}/Note(${value.event.id})`}
            event={{
              id: value.event.id,
              content: value.event.extendedProps?.content,
              start: value.event.startStr,
              end: value.event.endStr,
              alert_this_day: value.event.extendedProps?.alert_this_day,
              is_blocking_time: value.event.extendedProps?.is_blocking_time,
              selected_resources: [value.event.getResources()[0].id],
              display: value.event.display,
              history: value.event.extendedProps?.history,
            }}
          />
        )
      case 'blockTime':
        return (
          <BlockedTime
            key={value.event.id}
            event={{
              id: value.event.id,
              content: value.event.extendedProps?.content,
              start: value.event.startStr,
              end: value.event.endStr,
              alert_this_day: value.event.extendedProps?.alert_this_day,
              selected_resources: [value.event.getResources()[0].id],
              history: value.event.extendedProps?.history,
            }}
            viewPath={`${props.viewPath}/BlockedTime(${value.event.id})`}
          />
        )
      case 'candidate':
        return (
          <AppointmentCandidate
            key={value.event.id}
            event={{
              id: value.event.id,
              title: value.event.title,
              start: value.event.startStr,
              end: value.event.endStr,
              selected: value.event.extendedProps.selected,
              description: value.event.extendedProps.description,
              provider_id: value.event.extendedProps.providerId,
            }}
            viewPath={`${props.viewPath}/AppointmentCandidate(${value.event.id})`}
          />
        )
      case 'candidate-shadow':
        return (
          <AppointmentCandidateShadow
            key={value.event.id}
            event={{
              id: value.event.id,
              title: value.event.title,
              start: value.event.startStr,
              end: value.event.endStr,
              selected: value.event.extendedProps.selected,
              description: value.event.extendedProps.description,
            }}
            viewPath={`${props.viewPath}/AppointmentCandidateShadow(${value.event.id})`}
          />
        )
      case 'candidate-untemplated':
        return (
          <AppointmentCandidateUntemplated
            key={value.event.id}
            event={{
              id: value.event.id,
              title: value.event.title,
              start: value.event.startStr,
              end: value.event.endStr,
              description: value.event.extendedProps.description,
            }}
            viewPath={`${props.viewPath}/AppointmentCandidateUntemplated(${value.event.id})`}
          />
        )
      case 'candidate-untemplated-hover':
        return (
          <AppointmentCandidateUntemplatedHover
            key={value.event.id}
            event={{
              id: value.event.id,
              title: value.event.title,
              start: value.event.startStr,
              end: value.event.endStr,
              description: value.event.extendedProps.description,
              config: value.event.extendedProps.config,
            }}
            viewPath={`${props.viewPath}/AppointmentCandidateUntemplatedHover(${value.event.id})`}
          />
        )
      case 'appointment-type':
        return (
          <AppointmentType
            key={value.event.id}
            event={{
              id: value.event.id,
              title: value.event.title,
              start: value.event.start,
              end: value.event.end,
              ...value.event.extendedProps,
            }}
            viewPath={`${props.viewPath}/AppointmentType(${value.event.id})`}
          />
        )
      case 'appointment-type-candidate':
        return (
          <AppointmentTypeCandidate
            key={value.event.id}
            event={{
              id: value.event.id,
              start: value.event.start,
              end: value.event.end,
            }}
            viewPath={`${props.viewPath}/AppointmentType(${value.event.id})`}
          />
        )
      case 'closed':
        return (
          <Closed
            key={value.event.id}
            event={{
              id: value.event.id,
              start: value.event.start,
              end: value.event.end,
            }}
            viewPath={`${props.viewPath}/Closed(${value.event.id})`}
          />
        )
      default:
        return null
    }
  }

  /**
   * Render resource (column header) custom component
   * @param {import('@fullcalendar/resource').ResourceLabelContentArg} value
   */
  function renderResourceContent(value) {
    return (
      <Resource
        viewPath={props.viewPath}
        resource={{ ...value.resource.extendedProps }}
        isPrintReady={false}
      />
    )
  }

  /**
   * Render slot (time) label custom component
   * @param {import('@fullcalendar/core').SlotLabelContentArg} value
   */
  function renderSlotLabelContent(value) {
    return <SlotLabel viewPath={props.viewPath} time={value.text} />
  }

  /**
   * Render now indicator custom label
   * @param {import('@fullcalendar/core').NowIndicatorContentArg} value
   */
  function renderNowIndicatorContent(value) {
    if (value.isAxis) {
      let nowIndicator = document.querySelector(
        '.fc-timegrid-now-indicator-arrow'
      )
      if (nowIndicator) {
        let timeLabel = document.querySelector(
          '.fc-timegrid-now-indicator-time'
        )
        if (!timeLabel) {
          timeLabel = document.createElement('div')
          timeLabel.className = 'fc-timegrid-now-indicator-time'
          nowIndicator.appendChild(timeLabel)
        }
        timeLabel.textContent = formatTzDate(
          currentTzDate(timeZoneId),
          'h:mm a',
          timeZoneId
        )
      }
    }
  }

  return {
    renderEventContent,
    renderResourceContent,
    renderSlotLabelContent,
    renderNowIndicatorContent,
  }
}
